/* eslint-disable react-hooks/exhaustive-deps */
import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation,
} from "@apollo/client";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { v4 } from "uuid";
import {
  myOrders,
  orderStatusChanged,
  profile,
  myCarts as myCartsQuery,
  saveCart as saveCartMutationDoc,
  deleteCart as deleteCartMutationDoc,
  mergeCarts as mergeCartsMutationDoc,
} from "../apollo/server";
import CartDebug from "../utils/cartDebug";
import {
  SESSION_EXPIRED_EVENT,
  isTokenExpired,
} from "../utils/session";

const PROFILE = gql`
  ${profile}
`;

const ORDERS = gql`
  ${myOrders}
`;
const SUBSCRIPTION_ORDERS = gql`
  ${orderStatusChanged}
`; // rename this 'subscriptionOrders' when user context is complete

// Server-side carts, so a cart follows the customer between devices.
const MY_CARTS = gql`
  ${myCartsQuery}
`;
const SAVE_CART = gql`
  ${saveCartMutationDoc}
`;
const DELETE_CART = gql`
  ${deleteCartMutationDoc}
`;
const MERGE_CARTS = gql`
  ${mergeCartsMutationDoc}
`;

const UserContext = React.createContext({});

export const UserProvider = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const client = useApolloClient();
  // Read the stored token through the expiry check. A customer coming back
  // after the 30 day JWT lifetime used to boot straight into a signed-in shell
  // whose profile, phone and saved addresses were all empty, then hit a bare
  // "Unauthenticated" error at Place Order. Now the dead token never counts as
  // a session: they land signed out, with an explanation.
  const [initialSession] = useState(() => {
    const stored = localStorage.getItem("token");
    const expired = !!stored && isTokenExpired(stored);
    if (expired) localStorage.removeItem("token");
    return { token: expired ? null : stored, expired };
  });
  const [token, setToken] = useState(initialSession.token);
  // Distinguishes "signed out because the session ran out" from "never signed
  // in", so the login screen can say which one it is.
  const [sessionExpired, setSessionExpired] = useState(initialSession.expired);
  const [cart, setCart] = useState([]); // use initial state of cart here
  const [restaurant, setRestaurant] = useState(null);
  // Carts parked for other restaurants, DoorDash-style: switching restaurants
  // used to wipe the cart, so anything built up at the first place was lost.
  // Shape: { [restaurantId]: { items, meta: { _id, name, slug, image }, updatedAt } }
  const [savedCarts, setSavedCarts] = useState({});
  // Name/slug/image of the restaurant the ACTIVE cart belongs to, captured when
  // items are added. Parked carts need it to render a list without refetching
  // every restaurant, and by the time we park one we're already looking at a
  // different restaurant's data.
  const cartMetaRef = useRef(null);
  const [
    fetchProfile,
    {
      called: calledProfile,
      loading: loadingProfile,
      error: errorProfile,
      data: dataProfile,
    },
  ] = useLazyQuery(PROFILE, {
    fetchPolicy: "network-only",
    onCompleted,
    onError,
  });

  const [
    fetchOrders,
    {
      called: calledOrders,
      loading: loadingOrders,
      error: errorOrders,
      data: dataOrders,
      networkStatus: networkStatusOrders,
      fetchMore: fetchMoreOrders,
      subscribeToMore: subscribeToMoreOrders,
    },
  ] = useLazyQuery(ORDERS, {
    fetchPolicy: "network-only",
    onCompleted,
    onError,
  });

  // Sign-in boot. Orders are deliberately NOT fetched here.
  //
  // `myOrders` is the heaviest query a customer ever issues — measured at
  // 1.1MB of payload, 3.7s round trip, with `SELECT orders` alone taking 2.4s —
  // and it was running on every page load for every signed-in customer,
  // including pages that never show an order. Two things came of that: on a
  // single-threaded server it starved everything sharing the event loop (the
  // restaurant list went from 256ms to 6.4s on the same second, doing identical
  // work), and because `isLoading` waited for it, checkout held a spinner for
  // its whole duration too.
  //
  // The three screens that actually read `orders` — MyOrders, OrderDetail and
  // GetHelp — request it themselves on mount.
  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    let isSubscribed = true;
    (async () => {
      isSubscribed && setIsLoading(true);
      isSubscribed && (await fetchProfile());
      isSubscribed && setIsLoading(false);
    })();
    return () => {
      isSubscribed = false;
    };
  }, [token]);

  // The live order-status feed writes into the `orders` query's cache entry, so
  // it can only attach once that query has actually run. It used to hang off
  // the profile landing, which worked only because orders were fetched on boot;
  // now it waits for whichever screen asks for them first. Nothing is missed by
  // subscribing late — those screens fetch network-only, so they open on the
  // server's current truth and the subscription keeps it fresh from there.
  const subscribedRef = useRef(false);
  useEffect(() => {
    subscribedRef.current = false;
  }, [token]);
  useEffect(() => {
    if (subscribedRef.current) return;
    if (!dataProfile?.profile?._id || !dataOrders) return;
    subscribedRef.current = true;
    subscribeOrders();
  }, [dataProfile, dataOrders]);

  useEffect(() => {
    let isSubscribed = true;
    (async () => {
      const restaurant = localStorage.getItem("restaurant");
      const cart = localStorage.getItem("cartItems");
      isSubscribed && setRestaurant(restaurant || null);
      isSubscribed && setCart(cart ? JSON.parse(cart) : []);
      try {
        const parked = JSON.parse(localStorage.getItem("savedCarts") || "{}");
        isSubscribed && setSavedCarts(parked && typeof parked === "object" ? parked : {});
      } catch (e) {
        isSubscribed && setSavedCarts({});
      }
      try {
        cartMetaRef.current = JSON.parse(
          localStorage.getItem("cartRestaurantMeta") || "null"
        );
      } catch (e) {
        cartMetaRef.current = null;
      }
    })();
    return () => {
      isSubscribed = false;
    };
  }, []);

  function onCompleted({ profile, orders }) {}

  function onError(error) {
    console.log("error", error.message);
  }

  // The server rejected our token mid-session (expired, or invalidated).
  // Drop it and fall back to the signed-out UI. The cart and its restaurant stay
  // in localStorage on purpose: the customer signs back in and finishes the same
  // order rather than rebuilding it.
  const handleSessionExpired = useCallback(async () => {
    if (!localStorage.getItem("token") && !token) return;
    localStorage.removeItem("token");
    setToken(null);
    setSessionExpired(true);
    try {
      // clearStore, not resetStore: resetStore refetches every active query,
      // which would immediately fire the same authed queries again and loop.
      await client.clearStore();
    } catch (error) {
      console.log("error clearing store on session expiry", error);
    }
  }, [client, token]);

  useEffect(() => {
    const onExpired = () => handleSessionExpired();
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [handleSessionExpired]);

  const setTokenAsync = async (tokenReq, cb = () => {}) => {
    setToken(tokenReq);
    setSessionExpired(false);
    localStorage.setItem("token", tokenReq);
    cb();
  };

  const logout = async () => {
    try {
      localStorage.removeItem("token");
      setToken(null);
      setSessionExpired(false);
      await client.resetStore();
    } catch (error) {
      console.log("error on logout", error);
    }
  };

  const subscribeOrders = () => {

    try {
      const unsubscribeOrders = subscribeToMoreOrders({
        document: SUBSCRIPTION_ORDERS,
        variables: { userId: dataProfile.profile._id },
        updateQuery: (prev, { subscriptionData }) => {

          if (!subscriptionData.data) return prev;
          const payload = subscriptionData.data.orderStatusChanged;
          if (!payload) return prev;
          const updatedOrder = payload.order;
          if (!updatedOrder) return prev;
          // Coerce IDs to strings so a number/string mismatch doesn't cause
          // the same order to live in the list twice (which made the count
          // flicker up by 1 every time a status changed).
          const targetId = String(updatedOrder._id);
          const orders = prev?.orders || [];
          // Strip every stale copy first, then either insert at the front
          // (new order) or at the original position (status update).
          let originalIndex = -1;
          const filtered = orders.filter((o, i) => {
            if (String(o._id) === targetId) {
              if (originalIndex === -1) originalIndex = i;
              return false;
            }
            return true;
          });
          if (originalIndex === -1) {
            return { orders: [updatedOrder, ...filtered] };
          }
          const newList = [...filtered];
          newList.splice(originalIndex, 0, updatedOrder);
          return { orders: newList };
        },
      });
      client.onResetStore(unsubscribeOrders);
    } catch (error) {
      console.log("error subscribing order", error.message);
    }
  };

  const fetchMoreOrdersFunc = () => {
    if (networkStatusOrders === 7) {
      fetchMoreOrders({
        // offset is zero-based: the next page starts at exactly the number of
        // orders already loaded. The previous `+ 1` skipped one order at every
        // page boundary, dropping orders from the list.
        variables: { offset: dataOrders.orders.length },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          // Don't do anything if there weren't any new items
          if (!fetchMoreResult || fetchMoreResult.orders.length === 0) {
            return previousResult;
          }
          // Dedupe by _id so any overlap at the page boundary can't list the
          // same order twice (which would inflate the count).
          const seen = new Set(previousResult.orders.map((o) => String(o._id)));
          const merged = previousResult.orders.concat(
            fetchMoreResult.orders.filter((o) => !seen.has(String(o._id)))
          );
          return { orders: merged };
        },
      });
    }
  };

  // ── Cross-device cart sync ───────────────────────────────────────────────
  // Carts are still written to localStorage first (so a signed-out customer
  // keeps working exactly as before); when signed in, every change is mirrored
  // to the server and sign-in reconciles the two sides.
  const [saveCartToServer] = useMutation(SAVE_CART, {
    onError: (e) => console.log("saveCart failed", e.message),
  });
  const [deleteCartOnServer] = useMutation(DELETE_CART, {
    onError: (e) => console.log("deleteCart failed", e.message),
  });
  const [mergeCartsOnServer] = useMutation(MERGE_CARTS, {
    onError: (e) => console.log("mergeCarts failed", e.message),
  });
  const saveTimerRef = useRef(null);
  // When THIS device last changed the active cart. Sent as clientUpdatedAt so
  // the server can tell a genuinely fresh cart from a tab that has merely been
  // sitting open — stamping Date.now() at send time made every device claim to
  // be the newest, which would make any re-sync a coin flip.
  const lastChangeRef = useRef(
    Number(localStorage.getItem("cartUpdatedAt")) || 0
  );
  const markCartChanged = () => {
    lastChangeRef.current = Date.now();
    localStorage.setItem("cartUpdatedAt", String(lastChangeRef.current));
  };
  // Set while adopting the server's state, so the write-back effect doesn't
  // push what we just pulled and restamp it as a local change.
  const adoptingRef = useRef(false);
  const lastSyncRef = useRef(0);
  // How long a cleared cart stays ignored when the server echoes it back. Long
  // enough to cover the delete round-trip, short enough that a deliberate
  // re-order of the same restaurant on another device still syncs.
  const CLEARED_CART_TTL = 60000;

  // Carts cleared on this device, by restaurantId → when. The server delete is
  // fire-and-forget, and syncCarts runs on every window focus, so without this a
  // sync landing in that gap adopts the just-ordered cart straight back and the
  // badge refills.
  //
  // Kept in localStorage, not just in memory: placing an order navigates to the
  // order page and customers reload it to watch the status, which used to wipe
  // the guard and let the very next sync adopt the cart they had just paid for.
  const readClearedCarts = () => {
    try {
      return JSON.parse(localStorage.getItem("clearedCarts") || "{}");
    } catch (e) {
      return {};
    }
  };
  const clearedCartsRef = useRef(readClearedCarts());
  const writeClearedCarts = () => {
    try {
      localStorage.setItem("clearedCarts", JSON.stringify(clearedCartsRef.current));
    } catch (e) {
      /* private mode — the in-memory guard still holds for this tab */
    }
  };
  const isRecentlyCleared = (restaurantId) => {
    const at = clearedCartsRef.current[String(restaurantId)];
    return !!at && Date.now() - at <= CLEARED_CART_TTL;
  };
  // Adding to a restaurant's cart again is a deliberate new cart — stop
  // ignoring it, or the customer's second order of the evening never syncs.
  const unmarkCleared = (restaurantId) => {
    if (!restaurantId || !clearedCartsRef.current[String(restaurantId)]) return;
    delete clearedCartsRef.current[String(restaurantId)];
    writeClearedCarts();
  };

  const pushCart = (restaurantId, items, meta) => {
    if (!localStorage.getItem("token") || !restaurantId) return;
    // The last tap of +/- before Place Order leaves a debounced save in flight.
    // Landing after the order's delete, it recreated the row and the cart came
    // back. A cart that was just ordered is never worth writing.
    if (isRecentlyCleared(restaurantId)) return;
    saveCartToServer({
      variables: {
        cart: {
          restaurantId: String(restaurantId),
          items: JSON.stringify(items || []),
          meta: meta ? JSON.stringify(meta) : null,
          clientUpdatedAt: lastChangeRef.current || Date.now(),
        },
      },
    }).catch(() => {});
  };

  // The active cart changes on every tap of +/-; debounce so a burst of
  // quantity changes is one write, not six.
  useEffect(() => {
    if (!token || !restaurant) return;
    if (adoptingRef.current) {
      // This render is the server's own data landing — don't echo it back.
      adoptingRef.current = false;
      return;
    }
    markCartChanged();
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      pushCart(restaurant, cart, cartMetaRef.current);
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [cart, restaurant, token]);

  // Adopt the server's view wholesale after a merge/fetch. The active cart is
  // whatever this device already had; if it had none, the newest cart from the
  // server becomes active so the customer's cart genuinely follows them.
  const adoptServerCarts = (list) => {
    if (!Array.isArray(list)) return;
    adoptingRef.current = true;
    const nowTs = Date.now();
    const cleared = clearedCartsRef.current;
    let pruned = false;
    Object.keys(cleared).forEach((k) => {
      if (nowTs - cleared[k] > CLEARED_CART_TTL) {
        delete cleared[k];
        pruned = true;
      }
    });
    if (pruned) writeClearedCarts();
    const parsed = list
      .map((c) => {
        try {
          return {
            restaurantId: String(c.restaurantId),
            items: JSON.parse(c.items || "[]"),
            meta: c.meta ? JSON.parse(c.meta) : null,
            updatedAt: Number(c.clientUpdatedAt) || 0,
          };
        } catch (e) {
          return null;
        }
      })
      .filter(
        (c) =>
          c &&
          Array.isArray(c.items) &&
          c.items.length &&
          // Just ordered/emptied here — don't let the server hand it back.
          !cleared[String(c.restaurantId)]
      );

    const activeId = localStorage.getItem("restaurant");
    const hasLocalActive =
      activeId && JSON.parse(localStorage.getItem("cartItems") || "[]").length > 0;

    let nextActiveId = hasLocalActive ? activeId : null;
    if (!nextActiveId && parsed.length) {
      const newest = parsed.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a));
      nextActiveId = newest.restaurantId;
      lastChangeRef.current = newest.updatedAt;
      localStorage.setItem("cartUpdatedAt", String(newest.updatedAt));
      setCart(newest.items);
      setRestaurant(newest.restaurantId);
      localStorage.setItem("cartItems", JSON.stringify(newest.items));
      localStorage.setItem("restaurant", newest.restaurantId);
      cartMetaRef.current = newest.meta || null;
      if (newest.meta) {
        localStorage.setItem("cartRestaurantMeta", JSON.stringify(newest.meta));
      }
    } else if (nextActiveId) {
      // The server may hold a newer copy of the cart this device is on.
      const mine = parsed.find((c) => c.restaurantId === String(nextActiveId));
      if (mine && mine.updatedAt > (lastChangeRef.current || 0)) {
        // Another device edited this same cart more recently — take theirs.
        lastChangeRef.current = mine.updatedAt;
        localStorage.setItem("cartUpdatedAt", String(mine.updatedAt));
        setCart(mine.items);
        localStorage.setItem("cartItems", JSON.stringify(mine.items));
        if (mine.meta) {
          cartMetaRef.current = mine.meta;
          localStorage.setItem("cartRestaurantMeta", JSON.stringify(mine.meta));
        }
      }
    }

    const parked = {};
    parsed.forEach((c) => {
      if (String(c.restaurantId) === String(nextActiveId)) return;
      parked[c.restaurantId] = {
        items: c.items,
        meta: c.meta || { _id: c.restaurantId },
        updatedAt: c.updatedAt,
      };
    });
    persistSavedCarts(parked);
  };

  // Reconcile with the server: hand over everything this device has, take back
  // the merged set (newest wins per restaurant, decided server-side).
  //
  // Runs at sign-in AND whenever the tab is brought back to the front. Without
  // the second trigger a tab shows whatever it held when it loaded — two
  // devices on the same account would sit there disagreeing about the same
  // cart until one of them was reloaded.
  const syncCarts = (force = false) => {
    if (!localStorage.getItem("token")) return;
    const now = Date.now();
    // A tab switch is cheap to trigger and expensive to serve; don't hammer it.
    if (!force && now - lastSyncRef.current < 10000) return;
    lastSyncRef.current = now;

    const local = [];
    const activeId = localStorage.getItem("restaurant");
    let activeItems = [];
    try {
      activeItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    } catch (e) {
      activeItems = [];
    }
    if (activeId && activeItems.length) {
      local.push({
        restaurantId: String(activeId),
        items: JSON.stringify(activeItems),
        meta: localStorage.getItem("cartRestaurantMeta") || null,
        clientUpdatedAt: lastChangeRef.current || 0,
      });
    }
    try {
      Object.entries(
        JSON.parse(localStorage.getItem("savedCarts") || "{}")
      ).forEach(([id, c]) => {
        if (!c?.items?.length) return;
        local.push({
          restaurantId: String(id),
          items: JSON.stringify(c.items),
          meta: c.meta ? JSON.stringify(c.meta) : null,
          clientUpdatedAt: Number(c.updatedAt) || 0,
        });
      });
    } catch (e) {
      /* corrupt store — sync what we could read */
    }

    mergeCartsOnServer({ variables: { carts: local } })
      .then((res) => adoptServerCarts(res?.data?.mergeCarts))
      .catch(() => {});
  };

  useEffect(() => {
    if (!token) return;
    syncCarts(true);
    const onFocus = () => syncCarts();
    const onVisible = () => {
      if (document.visibilityState === "visible") syncCarts();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token]);

  const persistSavedCarts = (next) => {
    setSavedCarts(next);
    localStorage.setItem("savedCarts", JSON.stringify(next));
  };

  // Park the active cart under its restaurant instead of throwing it away.
  // Called when the customer starts ordering somewhere else. A no-op when the
  // cart is empty — an empty cart is nothing to come back to.
  const stashCart = () => {
    if (!restaurant || !cart.length) return;
    const next = {
      ...savedCarts,
      [restaurant]: {
        items: [...cart],
        meta: cartMetaRef.current || { _id: restaurant },
        updatedAt: Date.now(),
      },
    };
    persistSavedCarts(next);
    // Push before clearing the active slot: the debounced save watches `cart`,
    // and by the time it fires the cart is already empty.
    pushCart(restaurant, cart, cartMetaRef.current);
    setCart([]);
    setRestaurant(null);
    localStorage.removeItem("cartItems");
    localStorage.removeItem("restaurant");
    localStorage.removeItem("cartRestaurantMeta");
    cartMetaRef.current = null;
  };

  // Make a parked cart the active one, parking whatever is active first.
  const switchCart = (restaurantId) => {
    const target = savedCarts[restaurantId];
    if (!target) return null;
    unmarkCleared(restaurantId);
    const next = { ...savedCarts };
    delete next[restaurantId];
    // Park the current cart in the same pass so one write covers both sides.
    if (restaurant && cart.length) {
      next[restaurant] = {
        items: [...cart],
        meta: cartMetaRef.current || { _id: restaurant },
        updatedAt: Date.now(),
      };
      pushCart(restaurant, cart, cartMetaRef.current);
    }
    persistSavedCarts(next);
    setCart(target.items || []);
    setRestaurant(restaurantId);
    localStorage.setItem("cartItems", JSON.stringify(target.items || []));
    localStorage.setItem("restaurant", restaurantId);
    cartMetaRef.current = target.meta || null;
    if (target.meta) {
      localStorage.setItem("cartRestaurantMeta", JSON.stringify(target.meta));
    } else {
      localStorage.removeItem("cartRestaurantMeta");
    }
    return target.meta || null;
  };

  const discardCart = (restaurantId) => {
    const next = { ...savedCarts };
    delete next[restaurantId];
    persistSavedCarts(next);
    if (localStorage.getItem("token") && restaurantId) {
      deleteCartOnServer({
        variables: { restaurantId: String(restaurantId) },
      }).catch(() => {});
    }
  };

  // Everything that has to happen when a restaurant's active cart stops
  // existing, whatever emptied it: an order, "clear cart", or the last tap of
  // the minus stepper. Marking it cleared and dropping the debounced save come
  // FIRST, so a syncCarts landing while the server delete is still in flight
  // can't re-adopt it and the pending save can't recreate the row. Persisted,
  // so reloading the page keeps the guard.
  const teardownCart = (restaurantId) => {
    if (!restaurantId) return;
    clearedCartsRef.current[String(restaurantId)] = Date.now();
    writeClearedCarts();
    clearTimeout(saveTimerRef.current);
    localStorage.removeItem("restaurant");
    localStorage.removeItem("cartRestaurantMeta");
    cartMetaRef.current = null;
    // Drop the parked copy AND the server row, or the next sync pulls back the
    // cart that was just emptied.
    if (savedCarts[restaurantId]) discardCart(restaurantId);
    else if (localStorage.getItem("token")) {
      deleteCartOnServer({
        variables: { restaurantId: String(restaurantId) },
      }).catch(() => {});
    }
  };

  const clearCart = () => {
    CartDebug.log('UserContext', 'clearCart CALLED', { currentCartLength: cart.length });
    teardownCart(restaurant);
    setCart([]);
    setRestaurant(null);
    localStorage.removeItem("cartItems");
    // Unconditional, unlike teardownCart's: clearCart is also the reset when
    // there is no active restaurant in state but stale keys are still on disk.
    localStorage.removeItem("restaurant");
    localStorage.removeItem("cartRestaurantMeta");
  };

  const addQuantity = async (key, quantity = 1) => {
    const cartIndex = cart.findIndex((c) => c.key === key);
    cart[cartIndex].quantity += quantity;
    setCart([...cart]);
    localStorage.setItem("cartItems", JSON.stringify([...cart]));
  };

  const deleteItem = async (key) => {
    const cartIndex = cart.findIndex((c) => c.key === key);
    if (cartIndex > -1) {
      cart.splice(cartIndex, 1);
      const items = [...cart.filter((c) => c.quantity > 0)];
      // Removing the LAST line empties the cart just as surely as "clear cart"
      // does. Without the same teardown the server row survives, and because
      // the write-back effect bails once `restaurant` is null it is never
      // overwritten either — so the next sync hands the cart straight back.
      if (items.length === 0) {
        teardownCart(restaurant);
        setRestaurant(null);
      }
      setCart(items);
      localStorage.setItem("cartItems", JSON.stringify(items));
    }
  };

  const removeQuantity = async (key) => {
    const cartIndex = cart.findIndex((c) => c.key === key);
    cart[cartIndex].quantity -= 1;
    const items = [...cart.filter((c) => c.quantity > 0)];
    // Stepping the last item down to zero empties the cart — same teardown as
    // "clear cart", or the server keeps the row and a refresh restores it.
    if (items.length === 0) {
      teardownCart(restaurant);
      setRestaurant(null);
    }
    setCart(items);
    localStorage.setItem("cartItems", JSON.stringify(items));
  };

  const checkItemCart = (itemId) => {
    const cartIndex = cart.findIndex((c) => c._id === itemId);
    if (cartIndex < 0) {
      return {
        exist: false,
        quantity: 0,
      };
    } else {
      return {
        exist: true,
        quantity: cart[cartIndex].quantity,
        key: cart[cartIndex].key,
      };
    }
  };

  const numberOfCartItems = () => {
    return cart
      .map((c) => c.quantity)
      .reduce(function (a, b) {
        return a + b;
      }, 0);
  };

  const addCartItem = async (
    _id,
    variation,
    quantity = 1,
    addons = [],
    clearFlag,
    specialInstructions = "",
    replaceKey = null,
    menuId = null
  ) => {
    CartDebug.log('UserContext', 'addCartItem START', {
      _id,
      variation,
      quantity,
      addons: addons.length,
      clearFlag,
      currentCartLength: cart.length,
      currentRestaurant: restaurant
    });

    const cartItems = clearFlag ? [] : (replaceKey ? cart.filter((c) => c.key !== replaceKey) : [...cart]);
    const newItem = {
      key: v4(),
      _id,
      quantity: quantity,
      variation: {
        _id: variation,
      },
      addons,
      specialInstructions,
      // The advanced menu this line was ordered from — checkout sends it so the
      // server charges the correct per-menu markup. Persisted in localStorage so
      // it survives refresh. Null for traditional items / no-markup menus.
      menuId,
    };
    cartItems.push(newItem);

    CartDebug.log('UserContext', 'addCartItem - Updating cart', {
      newCartLength: cartItems.length,
      newItem: { ...newItem, addons: newItem.addons.length }
    });

    localStorage.setItem("cartItems", JSON.stringify([...cartItems]));
    setCart([...cartItems]);

    CartDebug.log('UserContext', 'addCartItem END', {
      finalCartLength: cartItems.length
    });
  };

  const updateCart = async (cart) => {
    setCart(cart);
    localStorage.setItem("cartItems", JSON.stringify(cart));
  };

  const setCartRestaurant = async (id, meta = null) => {
    // Ordering here again is a new cart, not the one that was just paid for.
    unmarkCleared(id);
    setRestaurant(id);
    localStorage.setItem("restaurant", id);
    // Callers on a restaurant page pass its name/slug/image so a parked cart
    // can be listed later without another query.
    if (meta) {
      cartMetaRef.current = meta;
      localStorage.setItem("cartRestaurantMeta", JSON.stringify(meta));
    }
  };

  return (
    <UserContext.Provider
      value={{
        isLoggedIn: !!token,
        sessionExpired,
        loadingProfile: loadingProfile && calledProfile,
        errorProfile,
        // Gated on `token` as well: once a session is dropped, the lazy query's
        // last result must not keep a stale name/phone/address on screen.
        profile:
          token && dataProfile && dataProfile.profile
            ? dataProfile.profile
            : null,
        setTokenAsync,
        logout,
        loadingOrders: loadingOrders && calledOrders,
        errorOrders,
        orders:
          token && dataOrders && dataOrders.orders ? dataOrders.orders : [],
        fetchOrders,
        fetchMoreOrdersFunc,
        networkStatusOrders,
        cart,
        // What the active cart was started with: the restaurant's name/slug/
        // image and, for a food-truck stop, stopId/stopLabel/truckName. Checkout
        // and the menu read the stop from here — each stop has its own menu.
        getCartMeta: () => cartMetaRef.current,
        cartCount: numberOfCartItems(),
        // Everything parked elsewhere. Kept separate from cartCount because
        // checkout keys its empty state off the ACTIVE cart — folding parked
        // items into that number would render checkout for a cart that isn't
        // open. The header adds the two together for its badge.
        otherCartsCount: Object.entries(savedCarts).reduce(
          (n, [id, c]) =>
            id === restaurant
              ? n
              : n + (c?.items || []).reduce((m, i) => m + (i.quantity || 0), 0),
          0
        ),
        clearCart,
        updateCart,
        addQuantity,
        removeQuantity,
        addCartItem,
        checkItemCart,
        deleteItem,
        restaurant,
        setCartRestaurant,
        // Parked carts for other restaurants.
        savedCarts,
        otherCarts: Object.entries(savedCarts)
          .filter(([id, c]) => id !== restaurant && (c?.items?.length ?? 0) > 0)
          .map(([id, c]) => ({
            restaurantId: id,
            items: c.items,
            meta: c.meta || { _id: id },
            updatedAt: c.updatedAt || 0,
            count: c.items.reduce((n, i) => n + (i.quantity || 0), 0),
          }))
          .sort((a, b) => b.updatedAt - a.updatedAt),
        stashCart,
        switchCart,
        discardCart,
        isLoading,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export const UserConsumer = UserContext.Consumer;
export default UserContext;
